import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Event as EventIcon,
    Warning as WarningIcon,
} from "@mui/icons-material";

import { useEmployees } from "../../../../hooks/useEmployees";
import { useVehicles } from "../../../../hooks/useVehicles";
import { useProjectDocuments } from "../../../../hooks/useProjectDocuments";

const getDocumentStatus = (doc, manifestEndDate) => {
    if (!doc?.expirationDate) return "NO REGISTRADO";

    const expDate = new Date(doc.expirationDate);
    const endDate = new Date(manifestEndDate);
    const today = new Date();
    const warningLimit = new Date();
    warningLimit.setDate(today.getDate() + 30);

    if (Number.isNaN(expDate.getTime())) return "NO REGISTRADO";
    if (expDate < today) return "VENCIDO";
    if (expDate < endDate) return "NO CUMPLE";
    if (expDate <= warningLimit) return "POR VENCER";

    return "VIGENTE";
};

const isDocumentValid = (status) => ["VIGENTE", "POR VENCER"].includes(status);

const statusConfig = {
    VIGENTE: { icon: <CheckCircleIcon color="success" />, color: "success" },
    "POR VENCER": { icon: <WarningIcon color="warning" />, color: "warning" },
    VENCIDO: { icon: <ErrorIcon color="error" />, color: "error" },
    "NO CUMPLE": { icon: <ErrorIcon color="warning" />, color: "warning" },
    "NO REGISTRADO": { icon: <ErrorIcon color="disabled" />, color: "default" },
};

const AssignForm = ({
    open,
    handleClose,
    manifestId,
    manifestfecfin,
    type,
    onAssign,
}) => {
    const [selectedOption, setSelectedOption] = useState(null);
    const [validDocs, setValidDocs] = useState([]);
    const [loadingValidation, setLoadingValidation] = useState(false);
    const [isInvitado, setIsInvitado] = useState(false);

    const { employees = [], isLoading: loadingEmployees } = useEmployees();
    const { vehicles = [], isLoading: loadingVehicles } = useVehicles();
    const { validateDocuments } = useProjectDocuments();

    const conductorID = Number(process.env.REACT_APP_ROL_CONDUCTOR_ID);

    const options = useMemo(() => {
        if (type === "employee") {
            return employees.filter((emp) => Number(emp.rolid) === conductorID);
        }
        return vehicles;
    }, [type, employees, vehicles, conductorID]);

    const isLoading = loadingEmployees || loadingVehicles;
    const selectedId = type === "employee" ? selectedOption?.empid : selectedOption?.vehid;

    const docsResume = useMemo(() => {
        const statuses = validDocs.map((doc) => getDocumentStatus(doc, manifestfecfin));

        return {
            total: validDocs.length,
            valid: statuses.filter(isDocumentValid).length,
            invalid: statuses.filter((s) => !isDocumentValid(s)).length,
            allValid: validDocs.length > 0 && statuses.every(isDocumentValid),
        };
    }, [validDocs, manifestfecfin]);

    useEffect(() => {
        if (!open) return;
        setSelectedOption(null);
        setValidDocs([]);
        setLoadingValidation(false);
        setIsInvitado(false);
    }, [open, type]);

    const handleValidation = async () => {
        if (!selectedId) return;

        try {
            setLoadingValidation(true);
            const docs = await validateDocuments(manifestId, selectedId, type);
            setValidDocs(Array.isArray(docs) ? docs : []);
        } finally {
            setLoadingValidation(false);
        }
    };

    const handleSubmit = () => {
        if (!selectedId) return;
        if (!isInvitado && !docsResume.allValid) return;

        onAssign({
            id: selectedId,
            isInvitado,
        });
    };

    const title = type === "employee" ? "Asignar conductor" : "Asignar vehículo";

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
            <DialogTitle>
                <Typography variant="h6" fontWeight="bold">
                    {title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Selecciona el recurso y valida su documentación antes de asignarlo.
                </Typography>
            </DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2}>
                    <Autocomplete
                        options={options}
                        loading={isLoading}
                        value={selectedOption}
                        onChange={(_, value) => {
                            setSelectedOption(value);
                            setValidDocs([]);

                            if (type === "employee") {
                                setIsInvitado(Boolean(value?.isinv));
                            }
                        }}
                        getOptionLabel={(option) => {
                            if (!option) return "";
                            if (type === "employee") {
                                return `${option.pernom || ""} ${option.perape || ""} - ${option.nomusu || ""}`.trim();
                            }
                            return `${option.vehmat || ""} - ${option.vehmar || ""} ${option.vehmod || ""}`.trim();
                        }}
                        isOptionEqualToValue={(option, value) => {
                            if (type === "employee") return String(option.empid) === String(value.empid);
                            return String(option.vehid) === String(value.vehid);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={type === "employee" ? "Conductor" : "Vehículo"}
                                helperText={isLoading ? "Cargando opciones..." : "Seleccione una opción."}
                            />
                        )}
                        disabled={isLoading}
                    />

                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, bgcolor: "#fafafa" }}>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                            <Box>
                                <Typography variant="body2" fontWeight="bold">
                                    Validación documental
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Requerida salvo que el recurso sea marcado como invitado.
                                </Typography>
                            </Box>

                            <Button
                                variant="outlined"
                                onClick={handleValidation}
                                disabled={!selectedId || loadingValidation}
                            >
                                {loadingValidation ? <CircularProgress size={20} /> : "Validar"}
                            </Button>
                        </Stack>

                        {validDocs.length > 0 && (
                            <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap" useFlexGap>
                                <Chip size="small" label={`${docsResume.total} documentos`} />
                                <Chip size="small" color="success" label={`${docsResume.valid} válidos`} />
                                <Chip size="small" color={docsResume.invalid > 0 ? "error" : "default"} label={`${docsResume.invalid} observados`} />
                            </Stack>
                        )}
                    </Paper>

                    <List dense disablePadding>
                        {validDocs.map((doc, index) => {
                            const status = getDocumentStatus(doc, manifestfecfin);
                            const config = statusConfig[status] || statusConfig["NO REGISTRADO"];
                            const expirationDate = doc.expirationDate
                                ? new Date(doc.expirationDate).toLocaleDateString("es-PE")
                                : "Sin fecha";

                            return (
                                <ListItem
                                    key={`${doc.name}-${index}`}
                                    sx={{
                                        px: 1,
                                        borderRadius: 2,
                                        mb: 0.5,
                                        border: "1px solid #e0e0e0",
                                        bgcolor: "#fff",
                                    }}
                                >
                                    <ListItemIcon>{config.icon}</ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography variant="body2" fontWeight="bold">
                                                    {doc.name}
                                                </Typography>
                                                <Chip size="small" label={status} color={config.color} />
                                            </Stack>
                                        }
                                        secondary={
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <EventIcon fontSize="inherit" />
                                                <span>Vence: {expirationDate}</span>
                                            </Stack>
                                        }
                                    />
                                </ListItem>
                            );
                        })}
                    </List>

                    {validDocs.length === 0 && (
                        <Alert severity="info" variant="outlined">
                            Selecciona una opción y valida documentos para habilitar la asignación.
                        </Alert>
                    )}

                    <Divider />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isInvitado}
                                onChange={(e) => setIsInvitado(e.target.checked)}
                            />
                        }
                        label="Marcar como invitado"
                    />

                    {isInvitado && (
                        <Alert severity="warning" variant="outlined">
                            La asignación como invitado omite la validación documental estricta.
                        </Alert>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose}>Cancelar</Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!selectedId || (!isInvitado && !docsResume.allValid)}
                >
                    Asignar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AssignForm;